import { type TFunction } from "i18next";
import type { Checkbox as CheckboxPrimitive } from "radix-ui";

import type { Item } from "@/api/items";
import { useLocationName } from "@/api/locations";
import { usePlanningPolicyName } from "@/api/planningPolicy";
import { type ColumnDef } from "@/components/data-table/DataTable";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

export function useItemsColumns(t: TFunction): ColumnDef<Item>[] {
  const policyName = usePlanningPolicyName();
  const locationName = useLocationName();
  return [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            (table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() &&
                "indeterminate")) as CheckboxPrimitive.CheckedState
          }
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          aria-label={t("common.selectAll")}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("common.selectRow")}
        />
      ),
      enableSorting: false,
      size: 32,
    },
    {
      accessorKey: "sku",
      header: t("items.columnTitleSku"),
      cell: ({ row }) => (
        <span className="block w-full truncate">{row.original.sku}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "name",
      header: t("items.columnTitleName"),
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span>{row.original.name}</span>
          {row.original.labels && row.original.labels.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {row.original.labels.map((l) => (
                <Badge key={l.id} variant="outline">
                  {l.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      ),
      enableSorting: true,
    },
    {
      id: "policy",
      header: t("items.columnTitlePolicy"),
      cell: ({ row }) => {
        const planned = row.original.planningConfigs.filter(
          (c) => c.planningPolicy != null,
        );
        if (planned.length === 0)
          return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {planned.map((c) => (
              <Badge key={c.location} variant="secondary">
                {locationName(c.location)} · {policyName(c.planningPolicy)}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
    },
  ];
}
